import { useCallback } from 'react';
import { Alert, Platform } from 'react-native';
import { useAppDispatch } from '../store';
import { clearAuth } from '../store/slices/authSlice';
import { clearUserData } from '../store/slices/userSlice';
import { clearNotifications } from '../store/slices/notificationSlice';
import { authApi } from '../services/api/authApi';
import { useAuthContext } from '@coffeeplexity/shared';
import Toast from 'react-native-toast-message';
import { logger } from '../utils/logger';

export const useLogout = () => {
  const dispatch = useAppDispatch();
  const { logout: authContextLogout } = useAuthContext();

  const logout = useCallback(
    async (showConfirmation = false) => {
      const performLogout = async () => {
        try {
          logger.warn('logout1');

          // Call logout API to invalidate tokens on server
          try {
            await authApi.logout();
          } catch (error) {
            // Continue with local logout even if API call fails
            logger.warn(
              'Logout API call failed, continuing with local logout:',
              error
            );
          }

          // Clear Redux state first
          dispatch(clearAuth());
          dispatch(clearUserData());
          dispatch(clearNotifications());

          // Call AuthContext logout - this clears tokens and updates auth state
          // The RootNavigator will automatically switch to auth screen when isAuthenticated becomes false
          await authContextLogout();

          Toast.show({
            type: 'success',
            text1: 'Logged out successfully',
            text2: 'See you next time!',
          });
        } catch (error) {
          logger.error('Error during logout:', error);
          Toast.show({
            type: 'error',
            text1: 'Logout Error',
            text2: 'Please try again',
          });
        }
      };

      if (showConfirmation) {
        // Use browser confirm for web, Alert for native
        if (Platform.OS === 'web') {
          const confirmed = window.confirm('Are you sure you want to logout?');
          if (confirmed) {
            try {
              await performLogout();
            } catch (error) {
              logger.error('Logout failed:', error);
            }
          } else {
            logger.debug('Logout cancelled');
          }
        } else {
          Alert.alert(
            'Logout',
            'Are you sure you want to logout?',
            [
              {
                text: 'Cancel',
                style: 'cancel',
                onPress: () => {},
              },
              {
                text: 'Logout',
                style: 'destructive',
                onPress: async () => {
                  try {
                    await performLogout();
                  } catch (error) {
                    logger.error('Logout failed:', error);
                  }
                },
              },
            ],
            { cancelable: true }
          );
        }
      } else {
        try {
          await performLogout();
        } catch (error) {
          logger.error('Logout failed:', error);
        }
      }
    },
    [dispatch, authContextLogout]
  );

  return { logout };
};
